import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponseHeaders,
  RawAxiosResponseHeaders,
} from 'axios'

import { IForm } from 'core-components/classes/models/IForm'
import { NotificationController } from 'core-components/library/popup'
import { AppHTTPRequest, appHTTPRequestBus } from 'src/core/classes/eventBuses/AppEventBus'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { clearTokens, getIdentityTokens, getTokens } from 'src/core/classes/helpers/StorageHelper'
import { IHttpRequestArgs } from 'src/core/classes/models/IHttpRequestArgs'
import { ITokenData } from 'src/core/classes/models/ITokenData'
import { authService } from 'src/core/classes/services/AuthService'
import settings from 'src/core/config/hostConfig'
import Logger from 'src/core/utils/Logger'

import {
  MAX_ERROR_REQUESTS_COUNT,
  RETRY_429_BASE_DELAY_MS,
  RETRY_429_MAX_DELAY_MS,
  TOO_MANY_REQUESTS_ERROR_MESSAGE,
} from './constants'
import { HttpHeaders } from './HttpHeaders'
import { decodeJwtPayload } from './httpRequest/decodeJwtPayload'
import { HttpQueue } from './httpRequest/HttpQueue'

axios.defaults.withCredentials = true

let blocker = Promise.resolve()
let isRefreshInProgress = false
const errorRequestCounter: Record<string, number> = {}
const error429RequestCounter: Record<string, number> = {}

export class HttpRequest {
  static SetBlockerCaller(newBl: any) {
    blocker = newBl
  }
  private identityToken?: boolean
  private headers: HttpHeaders
  private readonly client: AxiosInstance

  constructor(header: HttpHeaders) {
    this.headers = header
    this.client = axios.create()

    this.client.interceptors.request.use((config) => {
      // Don't override Authorization if explicitly set by caller (custom headers)
      if (config.headers.get('Authorization')) {
        return config
      }
      const [identityAccessToken] = getIdentityTokens()
      const [accessToken] = getTokens()
      const useIdentity = this.identityToken || settings.notExchangeToken
      const token = useIdentity ? identityAccessToken || accessToken : accessToken
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`)
      }
      return config
    })
  }

  public async get(data: IHttpRequestArgs) {
    return this.do('GET', data)
  }

  public async post(data: IHttpRequestArgs) {
    return this.do('POST', data)
  }

  public async put(data: IHttpRequestArgs) {
    return this.do('PUT', data)
  }

  public async delete(data: IHttpRequestArgs) {
    return this.do('DELETE', data)
  }

  private getHeaderValue(
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders | undefined,
    name: string
  ) {
    if (!headers) {
      return undefined
    }

    const getHeader = (headers: RawAxiosResponseHeaders | AxiosResponseHeaders, name: string) => {
      if (typeof headers.get === 'function') {
        return headers.get(name)
      }
      return headers[name]
    }

    return (
      getHeader(headers, name) ||
      getHeader(headers, name.toLowerCase()) ||
      getHeader(headers, name.toUpperCase())
    )
  }

  private get429RetryDelay(error: AxiosError, attempt: number) {
    const headers = error?.response?.headers

    const retryAfter = this.getHeaderValue(headers, 'Retry-After')
    if (retryAfter) {
      const seconds = Number(retryAfter)

      if (!Number.isNaN(seconds) && seconds >= 0) {
        return Math.min(Math.floor(seconds * 1000), RETRY_429_MAX_DELAY_MS)
      }

      const retryAfterDate = new Date(retryAfter).getTime()
      if (!Number.isNaN(retryAfterDate)) {
        return Math.min(Math.max(0, retryAfterDate - Date.now()), RETRY_429_MAX_DELAY_MS)
      }
    }

    const rateLimitReset =
      this.getHeaderValue(headers, 'X-RateLimit-Reset') ||
      this.getHeaderValue(headers, 'RateLimit-Reset')

    if (rateLimitReset) {
      const resetValue = Number(rateLimitReset)

      if (!Number.isNaN(resetValue) && resetValue >= 0) {
        const nowSeconds = Math.floor(Date.now() / 1000)

        if (resetValue > nowSeconds) {
          return Math.min((resetValue - nowSeconds) * 1000, RETRY_429_MAX_DELAY_MS)
        }

        return Math.min(resetValue * 1000, RETRY_429_MAX_DELAY_MS)
      }
    }

    return Math.min(RETRY_429_BASE_DELAY_MS * 2 ** attempt, RETRY_429_MAX_DELAY_MS)
  }

  protected async do(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    {
      payload = {},
      cancelObject,
      url,
      force = false,
      signal,
      identityToken,
      onUploadProgress,
    }: IHttpRequestArgs
  ): Promise<any> {
    this.identityToken = identityToken

    if (!force) {
      try {
        await blocker
      } catch (e) {
        Logger.warn('Blocker error', e)
        return {}
      }
    }

    const isValidToken = this.checkToken()
    if (!isValidToken) {
      return {}
    }

    const options: AxiosRequestConfig = {
      method,
      url,
      headers: this.headers.toObject(),
      params: {},
      signal,
      data: undefined,
      onUploadProgress: onUploadProgress,
      cancelToken: new axios.CancelToken((c) => {
        if (cancelObject) {
          cancelObject.setCancelFunction(c)
        }
      }),
      responseType: undefined,
    }

    if (url.includes('/export')) {
      options.responseType = 'blob'
    } else {
      delete options.responseType
    }

    if (payload.DeleteBlob) {
      delete options.responseType
      delete payload.DeleteBlob
    }
    if (method === 'GET') {
      options.params = payload
    } else {
      options.data = payload
    }
    const id = JSON.stringify({ ...options, cancelToken: undefined })
    const requestId = JSON.stringify({ url: options.url, method: options.method })
    const queueResult = await HttpQueue.add(id)
    try {
      if (queueResult.isError) {
        throw new Error(queueResult.result)
      }
      if (queueResult.isReady) {
        return queueResult.result
      }
      const response = await this.client(options)
      this.checkResponseError(response)
      if (errorRequestCounter[requestId]) {
        delete errorRequestCounter[requestId]
        this.sendNotification(`Connection restored`, 'success')
      }
      if (error429RequestCounter[requestId]) {
        delete error429RequestCounter[requestId]
      }
      queueResult.resolve(response)
      return response
    } catch (error: any) {
      queueResult.reject && queueResult.reject(error)
      if (axios.isCancel(error)) {
        return {
          cancelled: true,
        }
      }
      if (error && error.response && error.response.status === 404) {
        if (url === settings.passportEndpoint) {
          // Observable.getInstance().emit('regular-error', {
          //   data: error.response.data,
          // })
        }
      } else {
        Logger.warn(error)
      }
      if (error && error.response && error.response.status === 400) {
        // appHTTPRequestBus.notify(AppHTTPRequest.response400, {
        //   data: error.response.data,
        // })
      }
      if (error && error.response && error.response.status === 429) {
        error429RequestCounter[requestId] = error429RequestCounter[requestId] || 0

        const retryDelay = this.get429RetryDelay(error, error429RequestCounter[requestId])

        if (error429RequestCounter[requestId] < MAX_ERROR_REQUESTS_COUNT - 1) {
          return new Promise((resolve) => {
            setTimeout(() => {
              error429RequestCounter[requestId] += 1
              resolve(
                this.do(method, {
                  url,
                  payload,
                  cancelObject,
                  force: true,
                  signal,
                  identityToken,
                  onUploadProgress,
                })
              )
            }, retryDelay)
          })
        }

        delete error429RequestCounter[requestId]
        this.sendNotification(TOO_MANY_REQUESTS_ERROR_MESSAGE)

        return {
          data: {
            Errors: TOO_MANY_REQUESTS_ERROR_MESSAGE,
          },
          status: error?.response?.status,
          cancelled: false,
        }
      }
      if (error && error.response && error.response.status === 401) {
        Logger.warn('Handle 401, setting blocker, trying to refresh token')
        if (cancelObject) {
          cancelObject.setCancelFunction(() => {})
        }
        if (!isRefreshInProgress && !authService.isTokenRefreshing()) {
          isRefreshInProgress = true
          blocker = new Promise<any>(async (resolve) => {
            try {
              if (!errorRequestCounter[requestId]) {
                this.sendNotification(`Please wait. Trying to restore the connection...`)
              }
              await authService.refreshToken()
              const [newToken] = getIdentityTokens()
              const [oldToken] = getTokens()
              const useIdentity = identityToken || settings.notExchangeToken
              const tokenResult = useIdentity ? newToken : oldToken
              if (tokenResult) {
                Logger.warn('Refresh token success, repeat request')
                errorRequestCounter[requestId] = errorRequestCounter[requestId] || 0
                if (errorRequestCounter[requestId] < MAX_ERROR_REQUESTS_COUNT - 1) {
                  setTimeout(() => {
                    errorRequestCounter[requestId] += 1
                    resolve(
                      this.do(method, {
                        url,
                        payload,
                        cancelObject,
                        force: true,
                        signal,
                        identityToken,
                      })
                    )
                  }, errorRequestCounter[requestId] * 100)
                } else {
                  this.sendNotification(
                    `Can’t send the request. Please try again or contact support`,
                    'error'
                  )
                  resolve({})
                }
                return
              } else {
                Logger.warn('No valid token after refresh')
                appHTTPRequestBus.notify(AppHTTPRequest.response401)
              }
            } catch (e) {
              Logger.warn('Refresh token exception', e)
              appHTTPRequestBus.notify(AppHTTPRequest.response401)
            } finally {
              isRefreshInProgress = false
            }
          })
          return blocker
        }

        try {
          await blocker
          return this.do(method, { url, payload, cancelObject, signal, identityToken })
        } catch (e) {
          Logger.warn('Blocker error second request', e)
          return {}
        }
      }

      Logger.warn(error)
      return {
        data: {
          Errors: error?.response?.data?.Message || error?.message || 'Unknown error',
        },
        responseBody: error?.response?.data,
        status: error?.response?.status,
        cancelled: false,
      }
    }
  }

  private checkToken = () => {
    if (settings.notExchangeToken) {
      return true
    }

    const [token] = getTokens()

    if (!token) {
      return true
    }

    try {
      const userData = decodeJwtPayload<ITokenData>(token)
      const isValidAppId = getAppId() === userData?.app_id

      if (!isValidAppId) {
        NotificationController.showError({ message: 'Invalid token' })
        clearTokens()
        this.redirectToMainPage()
        return false
      }

      return true
    } catch (e) {
      Logger.error(e)
    }

    NotificationController.showError({ message: 'Invalid token' })
    return false
  }

  private sendNotification(message: string, type: 'error' | 'success' = 'error') {
    if (type === 'error') {
      NotificationController.showError({ message })
    } else {
      NotificationController.showSuccess({ message })
    }
  }

  private redirectToMainPage = () => {
    const currentPathName = window.location.pathname
    const url = currentPathName.length > 1 ? `/#redirectPage=${currentPathName}` : '/'
    if (url !== window.location.pathname) {
      window.location.assign(url)
    }
  }

  private checkResponseError(response: IForm) {
    if (
      response &&
      response['data'] &&
      response['data']['success'] !== undefined &&
      !response['data']['success']
    ) {
      throw new Error(response['data']['message'] || 'api error')
    }
  }
}
